/**
 * A1 Bindungskurve (Workshop-Lücken 29.10.2026, Plan `2026-10-29-workshop-luecken.md` § Paket M) auf der Anfo-Wert-Seite:
 * je gezahltem Typ (Quartal, Jahr) eine kleine Grafik — Linie = sBG-Kurve des wirksamen Laufs (aus seinen gespeicherten
 * Parametern), Punkte = Kaplan-Meier mit HEUTIGEM Stand über dieselben Zahler wie der Fit. Grau unter 30 im Risiko.
 * Rechnungsnummer rechnerisch (bezahlt ÷ Betrag je Rechnung). Anteile, kein Euro — brutto/netto spielt keine Rolle.
 * Quelle: `bindungService.ts`, eigener Endpunkt (`/umsatz/anfo-wert/bindung`).
 */
import { FC } from 'react';
import { Box, useTheme } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import { Legende, TipRow, achse, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { Hinweis, int, Laden, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { formatBerlinWallclock } from '../../../helpers/auswertungenData';
import { BindungPunkt, BindungTyp, fetchBindung } from '../../../helpers/umsatzData';

const TYP_NAME: Record<'Q' | 'U', string> = { Q: 'Quartalsbezug', U: 'Jahresbezug' };

const pct = (v: number | null) => (v === null ? '–' : formatPct(v * 100, 1));

/** Tabellenzeilen: Rechnung 1 ist Definition (100 %), steht nur in der Grafik. */
const zeilen = (t: BindungTyp) => t.punkte.filter((p) => p.rechnung > 1 && (p.gemessen !== null || p.rechnung <= 4));

const spalten: Spalte<BindungPunkt>[] = [
  { key: 'r', label: TERMS.rechnungRechnerisch.short, tip: TERMS.rechnungRechnerisch.tip, render: (p) => int(p.rechnung), sortValue: (p) => p.rechnung },
  { key: 'm', label: TERMS.bindungModell.short, tip: TERMS.bindungModell.tip, align: 'right', render: (p) => pct(p.modell), sortValue: (p) => p.modell },
  {
    key: 'g',
    label: TERMS.bindungGemessen.short,
    tip: TERMS.bindungGemessen.tip,
    align: 'right',
    render: (p) => (
      <Box component="span" sx={{ color: p.grau ? 'text.disabled' : undefined }}>
        {pct(p.gemessen)}
      </Box>
    ),
    sortValue: (p) => p.gemessen,
    unsicher: (p) => (p.grau && p.gemessen !== null ? 'Weniger als 30 Zahler im Risiko' : null),
  },
  { key: 'n', label: TERMS.imRisiko.short, tip: TERMS.imRisiko.tip, align: 'right', render: (p) => int(p.im_risiko), sortValue: (p) => p.im_risiko },
];

const Kurve: FC<{ t: BindungTyp }> = ({ t }) => {
  const viz = useVizStyle();
  const theme = useTheme();
  const daten = [
    { id: 'modell', data: t.punkte.map((p) => ({ x: p.rechnung, y: p.modell * 100, p })) },
    { id: 'gemessen', data: t.punkte.map((p) => ({ x: p.rechnung, y: p.gemessen === null ? null : p.gemessen * 100, p })) },
  ];
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{TYP_NAME[t.typ]}</Box>
      <Box sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
        {int(t.zahler_heute)} Zahler heute, davon {int(t.gekuendigt_heute)} gekündigt
      </Box>
      <Box sx={{ height: 200 }}>
        <ResponsiveLine
          data={daten}
          margin={{ top: 10, right: 16, bottom: 36, left: 44 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: 100 }}
          theme={viz.nivo}
          animate={false}
          enableGridX={false}
          gridYValues={[0, 25, 50, 75, 100]}
          axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: [0, 25, 50, 75, 100], format: achse('%') }}
          axisBottom={{ tickSize: 0, tickPadding: 6, legend: 'Rechnung', legendOffset: 28, legendPosition: 'middle' }}
          // Eigene Ebene: Modell als Linie ohne Punkte, Messung nur als Punkte (grau/hohl unter 30 im Risiko).
          layers={[
            'grid',
            'axes',
            ({ series, lineGenerator }) => {
              const modell = series.find((s) => s.id === 'modell');
              const gemessen = series.find((s) => s.id === 'gemessen');
              return (
                <g>
                  {modell && <path d={lineGenerator(modell.data.map((d) => d.position)) ?? undefined} fill="none" stroke={viz.series1} strokeWidth={2} />}
                  {gemessen?.data.map((d) => {
                    if (d.position.y === null || d.data.y === null) return null;
                    const grau = (d.data as unknown as { p: BindungPunkt }).p.grau;
                    return (
                      <circle
                        key={String(d.data.x)}
                        cx={d.position.x}
                        cy={d.position.y as number}
                        r={4.5}
                        fill={grau ? theme.palette.background.paper : viz.series2}
                        stroke={grau ? viz.muted : viz.series2}
                        strokeWidth={2}
                      />
                    );
                  })}
                </g>
              );
            },
            'slices',
          ]}
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            const p = (slice.points[0]?.data as unknown as { p: BindungPunkt }).p;
            return (
              <Box sx={{ ...(viz.nivo.tooltip as { container: object }).container, px: 1.5, py: 1 }}>
                <Box sx={{ fontWeight: 600, mb: 0.5 }}>Rechnung {p.rechnung}</Box>
                <TipRow color={viz.series1} label="Modell" value={pct(p.modell)} />
                <TipRow color={p.grau ? viz.muted : viz.series2} label="gemessen" value={p.gemessen === null ? 'noch niemand so weit' : `${pct(p.gemessen)} (${int(p.im_risiko)} im Risiko)`} />
              </Box>
            );
          }}
        />
      </Box>
      <SortableTable rows={zeilen(t)} columns={spalten} rowKey={(p) => String(p.rechnung)} maxHeight={null} eng />
    </Box>
  );
};

const BindungKarte: FC = () => {
  const { data, fehler } = useLaden(fetchBindung);
  const viz = useVizStyle();
  return (
    <ChartCard
      title="Wie lange Zahler zahlen"
      howToRead="Je Bezugsart: wie viele Zahler die zweite, dritte, … Rechnung bezahlen. Die Linie ist die Kurve, mit der das Modell die Zahlung bis Tag 75 auf 36 Monate hochrechnet. Die Punkte sind gemessen mit heutigem Stand über dieselben Zahler; hohle graue Punkte stützen sich auf weniger als 30 Zahler. Liegen die Punkte unter der Linie, rechnet das Modell mit zu viel Treue."
      terms={[
        { label: TERMS.bindungModell.label, tip: TERMS.bindungModell.tip },
        { label: TERMS.bindungGemessen.label, tip: TERMS.bindungGemessen.tip },
        { label: TERMS.imRisiko.label, tip: TERMS.imRisiko.tip },
        { label: TERMS.rechnungRechnerisch.label, tip: TERMS.rechnungRechnerisch.tip },
      ]}
      source="Parameter des wirksamen Anfo-Wert-Laufs (Bindungskurve), PL-X-Zahlungen der letzten 24 Monate"
      freshnessKey={['anfo_werte', 'plx_orders']}
    >
      {!data ? (
        <Laden fehler={fehler} />
      ) : !data.typen ? (
        <Hinweis>Kein wirksamer Lauf mit gespeicherter Bindungskurve.</Hinweis>
      ) : (
        <>
          <Legende
            items={[
              { label: 'Modell (Lauf)', color: viz.series1, shape: 'line' },
              { label: 'gemessen mit heutigem Stand', color: viz.series2 },
            ]}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 1 }}>
            {data.typen.map((t) => (
              <Kurve key={t.typ} t={t} />
            ))}
          </Box>
          <Hinweis>
            Linie: Lauf vom {data.run ? formatBerlinWallclock(data.run.as_of) : '–'} mit {data.typen.map((t) => `${int(t.n_lauf)} Zahlern im ${TYP_NAME[t.typ]}`).join(' und ')}. Punkte gemessen mit heutigem Stand; Rechnungsnummer
            rechnerisch (bezahlt ÷ Betrag je Rechnung). Abstand Punkt zu Linie ist nicht allein ein Fehler des Modells: Der Lauf hat mit dem Stand seines Tages gerechnet.
          </Hinweis>
        </>
      )}
    </ChartCard>
  );
};

export default BindungKarte;
